import type { ClientData, MeasureData } from '../interfaces';
import { currentConfig } from '../domainConfig';
import { metricChange } from '../../design-system/lib/metric-format';
import { isReading } from './measurementDelta';

export interface MeasurementPointLite {
  id: string;
  date: Date;
  value: number;
}

export interface MeasurementSeries {
  key: string;
  title: string;
  unit: string;
  values: MeasurementPointLite[];
  latest: number;
  /** Latest minus first, between the figures as displayed (metric-format.ts). */
  change: number;
  /**
   * The date of the latest reading, when it is older than the client's latest
   * scan. Weight is often missing from recent scans, and the card showed the
   * last weighed scan's 90 kg as though it were today's.
   */
  asOf?: Date;
}

const UNITS: Record<string, string> = {
  body_fat: '%',
  weight: 'kg',
};

/** Every circumference is centimetres; only weight and body fat differ. */
export const unitFor = (key: string): string =>
  UNITS[key] ?? (key === 'height' ? 'cm' : 'cm');

export const titleFor = (key: string, label: string): string =>
  key === 'body_fat' ? 'Body Fat' : label;

/**
 * One chronological series per configured measurement, zeroes dropped.
 *
 * Pulled out of TrendsTab so the Measurements and History sections read the
 * same numbers from the same place. A zero here means "not captured on this
 * scan" rather than a measured zero, which is why it is filtered rather than
 * plotted — a dropped-to-zero waist would otherwise draw a cliff.
 */
export function buildMeasurementSeries(
  measurements: MeasureData[],
  client: Pick<ClientData, 'gender'>,
): MeasurementSeries[] {
  const gender = client.gender === 'female' ? 'female' : 'male';
  const configured = currentConfig.measurements[gender] ?? [];

  const chronological = [...measurements].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const lastScan = chronological[chronological.length - 1];
  const lastScanAt = lastScan ? new Date(lastScan.createdAt).getTime() : 0;

  return configured
    .map((m, i) => {
      const values = chronological
        .map((d) => ({
          id: `${i}`,
          date: new Date(d.createdAt),
          value: d.measurements[m.key] as unknown,
        }))
        // The same rule the change pair uses (measurementDelta.ts), so the
        // card's latest and its change come from the scans every other
        // surface reads.
        .filter((p): p is MeasurementPointLite => isReading(m.key as string, p.value));

      const first = values[0];
      const last = values[values.length - 1];
      return {
        key: m.key as string,
        title: titleFor(m.key as string, m.label),
        unit: unitFor(m.key as string),
        values,
        latest: last ? last.value : 0,
        change: last ? metricChange(m.key as string, first.value, last.value) : 0,
        ...(last && last.date.getTime() < lastScanAt ? { asOf: last.date } : {}),
      };
    })
    .filter((s) => s.values.length > 0);
}
